import { ActionIcon, Anchor, Avatar, Badge, Box, Button, Flex, Grid, Group, Modal, NumberInput, Paper, Select, Stack, Text, Textarea, TextInput, Tooltip } from "@mantine/core"
import classes from "./add-dynamic-form.module.css"
import { FloppyDiskIcon, PencilSimpleIcon, PlusIcon, FileTextIcon, PackageIcon, PercentIcon, ImageIcon, ReceiptIcon, TextAaIcon, TrashIcon } from "@phosphor-icons/react";
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { IAuth } from '../../store/Auth'
import { IAdditionalLine } from '../../models/AdditionalLine'
import { IAttachment } from '../../models/Attachment'
import { ILinePlus } from '../../models/Line'
import { calcTermDiscount, currencyFormat, lineNetAmount, lineTaxAmount, lineVatAmount, termDiscountSuffix, unitTaxAmount, unitVatAmount } from "../../utils"
import type { ITermDiscount } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import { FileField } from "../form/file-field"
import { NumberCurrencyField } from "../form/number-currency-field"
import { ColumnType, Table } from "../table"
import RenderText from "../ui/render-text"
import { EmptyLines } from "../order/empty-lines"
import { OrderLedger } from "../order/order-ledger"
import { useVariantPricing } from "../order/use-variant-pricing"
import { VariantSelectorDrawer } from "../shared/variant-selector-drawer"
import { SupplierSalesAddDynamicAdditionalForm } from "./add-dynamic-additional-form"

type Props = {
  /** Two-column supplier create (Ord_CustomerOrder): no section nav, the canvas's six line columns,
   * the additional-items table only once a row exists. */
  compact?: boolean
  values?: any
  setFieldValue?: any
  errors?: any
  /**
   * Which side is creating the order. Buyer-side orders don't expose
   * supplier-only fields (shipping fee, order-level discount, additional
   * manual lines) — those are decided by the supplier when reviewing.
   */
  side?: "supplier" | "buyer"
  /**
   * Payment term (paymentTerm object). When provided, used for the payment
   * confirmation calc instead of `values.paymentTerm`. Buyer flow passes this
   * from businessData since the buyer doesn't own the paymentTerm in form state.
   */
  paymentData?: any
  /**
   * Payment-term discount rule of the term this order will be sent with. Buyer side
   * only — the buyer has no discount input, so the ledger just previews what
   * de-order-service will take off the order total (B.2.3).
   */
  termDiscount?: ITermDiscount | null
  /**
   * Description of the payment term whose rule filled the order-level discount in
   * (supplier side, B.8). Non-null only while the field still holds the auto-filled
   * value — set to null the moment the supplier edits or clears it.
   */
  autoFilledTermName?: string | null
}

// Scroll trigger offset — a section is treated as "passed" once its top reaches
// this distance from the viewport. Must be >= the section's scrollMarginTop
// (150) so that clicking a nav step still registers the destination as passed.
const SECTION_TRIGGER_OFFSET = 170

function useLineSpy(ids: string[]) {
  const [active, setActive] = useState(0)
  const handleScroll = useCallback(() => {
    // HMR / stale-closure defensive guard: bail if ids didn't make it through
    if (!ids || ids.length === 0) return
    // Track the last section whose top has scrolled above the trigger offset.
    // If we're near the page bottom, force the last section active so that
    // attachments lights up once the document can't scroll any further.
    let lastPassed = 0
    ids.forEach((id, i) => {
      const el = document.getElementById(id)
      if (!el) return
      if (el.getBoundingClientRect().top <= SECTION_TRIGGER_OFFSET) {
        lastPassed = i
      }
    })
    const scrollBottom = window.scrollY + window.innerHeight
    const docHeight = document.documentElement.scrollHeight
    if (docHeight - scrollBottom < 4) {
      lastPassed = ids.length - 1
    }
    setActive(lastPassed)
  }, [ids])
  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true })
    window.addEventListener("resize", handleScroll, { passive: true })
    handleScroll()
    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleScroll)
    }
  }, [handleScroll])
  return active
}

const LINE_STEPS_FULL = [
  { id: "line-section-products", titleKey: "supplierSales:sales.products", icon: <PackageIcon size={14} />, color: "var(--mantine-color-brand-6)", bg: "color-mix(in srgb, var(--mantine-color-brand-6) 8%, var(--mantine-color-white))" },
  { id: "line-section-additional", titleKey: "supplierSales:sales.additionalItems", icon: <TextAaIcon size={14} />, color: "var(--mantine-color-brand-6)", bg: "color-mix(in srgb, var(--mantine-color-brand-6) 8%, var(--mantine-color-white))" },
  { id: "line-section-summary", titleKey: "supplierSales:sales.orderSummary", icon: <ReceiptIcon size={14} />, color: "var(--mantine-color-brand-6)", bg: "color-mix(in srgb, var(--mantine-color-brand-6) 8%, var(--mantine-color-white))" },
  { id: "line-section-attachments", titleKey: "supplierSales:sales.attachments", icon: <FileTextIcon size={14} />, color: "var(--mantine-color-brand-6)", bg: "color-mix(in srgb, var(--mantine-color-brand-6) 8%, var(--mantine-color-white))" },
]

export function SupplierSalesAddDynamicForm({ compact = false, values, setFieldValue, errors, side = "supplier", paymentData, termDiscount = null, autoFilledTermName = null }: Props) {
  const { t } = useTranslation(["supplierSales", "common"])
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const now_date = new Date()
  const isBuyer = side === "buyer"
  const myBusinessId = user?.currentBusinessId
  const LINE_STEPS = isBuyer
    ? LINE_STEPS_FULL.filter((step) => step.id !== "line-section-additional")
    : LINE_STEPS_FULL
  const paymentTerm = paymentData ?? values?.paymentTerm
  // Memoize the list of section IDs the spy watches.
  // Depend on the stable `isBuyer` boolean rather than the LINE_STEPS array,
  // which gets a fresh reference every render and would keep invalidating spyIds
  const spyIds = React.useMemo(
    () =>
      isBuyer
        ? ["line-section-products", "line-section-summary", "line-section-attachments"]
        : ["line-section-products", "line-section-additional", "line-section-summary", "line-section-attachments"],
    [isBuyer]
  )
  const activeSection = useLineSpy(spyIds)
  // Map section id → index in the visible nav, so active-card styling
  // lines up even when buyer-mode hides the "Additional" section
  const productsIdx = spyIds.indexOf("line-section-products")
  const additionalIdx = spyIds.indexOf("line-section-additional")
  const summaryIdx = spyIds.indexOf("line-section-summary")
  const attachmentsIdx = spyIds.indexOf("line-section-attachments")
  const [edit, setEdit] = React.useState<string>("")
  const [action, setAction] = React.useState<string[]>([])
  const [drawerOpened, setDrawerOpened] = useState(false)

  const priceLines = useMemo(
    () => (values?.lines || []).map((l: any) => ({ variantId: l.variantId, quantity: l.quantity || 1 })),
    [values?.lines],
  )

  const sellerBizId = isBuyer ? values?.supplierId : myBusinessId
  const buyerBizId = isBuyer ? myBusinessId : values?.supplierId

  const { priceByVariant } = useVariantPricing(
    sellerBizId,
    buyerBizId,
    priceLines,
  )

  useEffect(() => {
    (values?.lines || []).forEach((l: any, i: number) => {
      const fresh = priceByVariant[l.variantId]
      if (fresh != null && fresh !== l.price) {
        setFieldValue(`lines.${i}.price`, fresh)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceByVariant])

  const columns = useHeader({
    t,
    edit,
    imageColumn: !compact,
    values,
    setFieldValue,
    setEdit,
    onClick: (key, item) => {
      if (key === "remove") {
        setFieldValue("lines", [...values.lines.filter((i: any, index: number) => item.index !== index)])
      } else if (key === "edit") {
        setAction(["edit", item])
      }
    },
  })

  const columnsAdditional = useHeaderAdditional({
    t,
    edit,
    setEdit,
    setFieldValue,
    values,
    errors,
    onClick: (key, item) => {
      if (key === "remove") {
        setFieldValue("additionalLines", [...values.additionalLines.filter((i: any, index: number) => item.index !== index)])
      } else if (key === "edit") {
        setAction(["edit", item])
      }
    },
  })

  const columnsAttachments = useHeaderAttachments({
    t,
    edit,
    setEdit,
    values,
    setFieldValue,
    errors: errors || {},
    onClick: (key, item) => {
      if (key === "remove") {
        setFieldValue("attachments", [...values.attachments.filter((i: any, index: number) => item.index !== index)])
      } else if (key === "edit") {
        setAction(["edit", item])
      }
    },
  })

  const len = Object.keys(values?.lines || [])?.length

  // Header row of the empty lines table, as the canvas lists it (OrdB_SupplierOrder).
  const emptyLineColumns = compact
    ? [
        t("common:table.action"),
        t("supplierSales:sales.productService"),
        t("supplierSales:sales.warehouse"),
        t("supplierSales:sales.orderQuantity"),
        t("supplierSales:sales.unitPrice"),
        t("supplierSales:sales.totalAmount"),
      ]
    : [
        t("common:table.action"),
        t("supplierSales:sales.image"),
        t("supplierSales:sales.productService"),
        t("supplierSales:sales.warehouse"),
        t("supplierSales:sales.orderQuantity"),
        t("supplierSales:sales.unitPrice"),
        t("supplierSales:sales.totalVat"),
        t("supplierSales:sales.totalAmount"),
      ]
  // VAT and tax per line stay in the summary; the narrow column shows the canvas's six,
  // the full-width one the eight of emptyLineColumns (only the line's total VAT).
  const hiddenColumns = compact
    ? ["#", t("supplierSales:sales.vatUnit"), t("supplierSales:sales.totalVat"), t("supplierSales:sales.taxUnit"), t("supplierSales:sales.totalTax")]
    : ["#", t("supplierSales:sales.vatUnit"), t("supplierSales:sales.taxUnit"), t("supplierSales:sales.totalTax")]
  const lineColumns = columns.filter((c) => !hiddenColumns.includes(c.title as string))

  // ---- Summary calculations ----
  // Line-level aggregates; formulas preserved from the legacy inline JSX so
  // the displayed totals don't shift when we migrate to OrderLedger.
  const summaryLines: any[] = values?.lines || []
  const summaryAddLines: any[] = values?.additionalLines || []
  const totalQty = summaryLines.reduce((s: number, i: any) => s + ((i?.quantity || 0) as number), 0)
  const orderTotalAmount = summaryLines.reduce((s: number, i: any) => s + ((i?.price || 0) as number) * ((i?.quantity || 0) as number), 0)
  const calculatedDiscount = summaryLines.reduce((s: number, i: any) => {
    const d = i?.discountType === "PERCENTAGE"
      ? ((i?.price || 0) * (i?.quantity || 0) * (i?.discountValue || 0)) / 100
      : (i?.quantity || 0) * (i?.discountValue || 0)
    return s + (d || 0)
  }, 0)
  const additionalSub = summaryAddLines.reduce((s: number, i: any) => s + ((i?.quantity || 0) * (i?.price || 0)), 0)
  const additionalDisc = summaryAddLines.reduce((s: number, i: any) => {
    const d = i?.discountType === "PERCENTAGE"
      ? ((i?.price || 0) * (i?.quantity || 0) * (i?.discountValue || 0)) / 100
      : (i?.discountValue || 0)
    return s + (d || 0)
  }, 0)
  const additionalTotal = summaryAddLines.length ? additionalSub - additionalDisc : 0

  // Grand total — mirrors de-order-service order/create.ts.
  // DSC-001: prices are VAT-INCLUSIVE, so the extracted VAT/tax must NOT be added
  // on top of the line total again (that showed 241,331 for an order the backend
  // stores as 221,220). A line total is simply price*qty minus its own discount.
  // ORDER_AMOUNT excludes the shipping fee — shipping is added after the
  // order-level discount, exactly as the backend does it.
  const lineTotals = summaryLines.map((i: any) => lineNetAmount(i))
  const orderAmount = lineTotals.reduce((s: number, v: number) => s + v, 0) + additionalTotal

  // Order-level discount. Supplier side: their own input (auto-filled from the
  // payment-term rule by B.8, still editable). Buyer side: no input at all — the
  // server applies the rule of the selected term, so this only previews it.
  const orderDiscountValue = Number(values?.discountValue) || 0
  const supplierOrderDiscount = Math.min(
    values?.discountType === "PERCENTAGE" ? (orderAmount * orderDiscountValue) / 100 : orderDiscountValue,
    orderAmount,
  )
  const orderLevelDiscount = isBuyer
    ? calcTermDiscount(orderAmount, termDiscount)
    : Math.max(0, supplierOrderDiscount)
  const grandTotal = orderAmount - orderLevelDiscount + (values?.shippingAmount || 0)

  // VAT and city tax are extracted OUT of the inclusive price, so they follow the
  // DISCOUNTED line total: the order-level discount is spread over the lines
  // pro-rata and both are recomputed from what is left, the same shape as
  // discount-allocation.ts.
  //
  // The gate is on vatType, NOT on the percent. create.ts:448 is
  // `vatType === "STANDARD_RATED" ? ... : 0` and create.ts:438 defaults the
  // percent to 10, so a VAT_FREE / VAT_ZERO variant still arrives carrying
  // vatPercent 10 — gating on the percent alone charged the buyer VAT on
  // exempt goods that the order then records as zero.
  const netOfShare = (index: number) => {
    const base = lineTotals[index] || 0
    const share = orderAmount > 0 ? (orderLevelDiscount * base) / orderAmount : 0
    return Math.max(0, base - share)
  }
  const calculatedVat = summaryLines.reduce(
    (s: number, i: any, index: number) => s + lineVatAmount(i, netOfShare(index)),
    0,
  )
  const calculatedTax = summaryLines.reduce(
    (s: number, i: any, index: number) => s + lineTaxAmount(i, netOfShare(index)),
    0,
  )

  // What the ledger shows on the discount row. The supplier's order-level discount
  // belongs there beside the line discounts; the buyer's payment-term discount gets
  // its own named row instead.
  const ledgerDiscount = isBuyer ? calculatedDiscount : calculatedDiscount + orderLevelDiscount

  // Payment confirmation — COD advance amount + date offset
  const codAdvance = summaryLines.length && paymentTerm?.condition === "COD"
    ? (grandTotal * (paymentTerm.advancePercent || 0)) / 100
    : null
  const paymentDateStr = paymentTerm?.condition === "COD"
    ? dateTimeSecFormat(new Date(now_date.getTime() + (paymentTerm?.confirmationDay || 0) * 86400000))
    : "-"

  const navRef = React.useRef<HTMLDivElement>(null)
  const [navFixed, setNavFixed] = React.useState(false)
  const [navLeft, setNavLeft] = React.useState(0)
  const [navWidth, setNavWidth] = React.useState(0)

  React.useEffect(() => {
    const onScroll = () => {
      if (!navRef.current) return
      const rect = navRef.current.getBoundingClientRect()
      setNavLeft(rect.left)
      setNavWidth(rect.width)
      setNavFixed(rect.top <= 68)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    onScroll()
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [])

  const navRow = (
    <Flex align="center" gap="lg" className={classes.tabsRow} role="tablist">
      {LINE_STEPS.map((step, i) => {
        const isActive = activeSection === i
        return (
          <button
            key={step.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={classes.tab}
            data-active={isActive || undefined}
            onClick={() => {
              const el = document.getElementById(step.id)
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
            }}
          >
            {t(step.titleKey)}
          </button>
        )
      })}
    </Flex>
  )

  return (
    <Box>
      {/* Section Nav - supplier flow only (matches the original BuyerCustomerDynamicForm UX) */}
      {!isBuyer && !compact && (
        <>
          <Box ref={navRef} mb="md" mt="md">
            {!navFixed && navRow}
            {navFixed && <Box style={{ height: 52 }} />}
          </Box>
          <Box
            className={classes.navFixed}
            style={{
              left: navLeft,
              width: navWidth,
              opacity: navFixed ? 1 : 0,
              transform: navFixed ? "translateY(0)" : "translateY(-100%)",
              pointerEvents: navFixed ? "auto" : "none",
            }}
          >
            {navRow}
          </Box>
        </>
      )}

      {/* 1. Бараа, ажил үйлчилгээ — full width; grey header row, «Та бараа оруулна уу.» when empty (OrdB_SupplierOrder) */}
      <Paper id="line-section-products" className={classes.sectionCard} data-flush data-active={(!isBuyer && activeSection === productsIdx) || undefined} mb="md">
        <Group justify="space-between" wrap="wrap" gap="sm" className={classes.cardHead}>
          <Text className={classes.cardTitle}>{t("supplierSales:sales.productService")}</Text>
          <Group gap="sm">
            <Button variant="default" onClick={() => setAction(["add", values])}>
              {t("supplierSales:sales.addProduct")}
            </Button>
            <Button variant="default" onClick={() => setDrawerOpened(true)}>
              {t("supplierSales:sales.addByPackage")}
            </Button>
          </Group>
        </Group>
        {(values.lines || []).length === 0 ? (
          <EmptyLines columns={emptyLineColumns} />
        ) : (
          <Box className={classes.linesTable}>
            <Table columns={lineColumns} name="values.lines" dataSource={values.lines} pagination={false} />
          </Box>
        )}
      </Paper>

      {/* 2. Нэмэлтээр — supplier only */}
      {!isBuyer && (
        <Paper id="line-section-additional" className={classes.sectionCard} data-flush data-active={(activeSection === additionalIdx) || undefined} mb="md">
          <Group justify="space-between" wrap="wrap" gap="sm" className={classes.cardHead}>
            <Text className={classes.cardTitle}>{t("supplierSales:sales.additionalItems")}</Text>
            <Button variant="default" leftSection={<PlusIcon size={16} />}
              onClick={() => {
                setFieldValue("additionalLines", [
                  ...(values?.additionalLines || []),
                  {
                    name: "",
                    unit: "",
                    price: null,
                    quantity: null,
                    discountType: "",
                    discountValue: null,
                    isEdit: true,
                    isNew: true,
                  },
                ])
              }}
            >
              {t("common:actions.add")}
            </Button>
          </Group>
          {(!compact || (values?.additionalLines || []).length > 0) && (
            <Box className={classes.linesTable}>
              <Table columns={columnsAdditional} name="values.additionalLines" dataSource={values.additionalLines} pagination={false} />
            </Box>
          )}
        </Paper>
      )}

      {/* Mantine ignores `hidden`: render only on error, as text, not a red card (O24). */}
      {errors?.items && (
        <Text fz={14} c="red.8" mb="md" role="alert">
          {t("supplierSales:sales.pleaseAddProducts")}
        </Text>
      )}

      {/* 3. Захиалгын нэгтгэл — one card: notes on the left half, the ledger on the right (OrdB_SupplierOrder) */}
      <Paper id="line-section-summary" className={classes.sectionCard} data-active={(!isBuyer && activeSection === summaryIdx) || undefined} p={20} mb="md">
        <Grid gap="xl">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Text className={classes.cardTitle} mb="sm">{t("supplierSales:sales.orderSummary")}</Text>
            <Stack gap="sm">
              <Textarea
                label={t("supplierSales:sales.customerAdditionalNote")}
                placeholder={t("supplierSales:sales.enterDescription")}
                maxLength={255}
                rows={3}
                value={values?.senderNote ?? ""}
                onChange={(e) => setFieldValue("senderNote", e.target.value)}
              />
              <Textarea
                label={t("supplierSales:sales.additionalNote")}
                placeholder={t("supplierSales:sales.enterNote")}
                maxLength={255}
                rows={3}
                value={values?.senderAdditionalNote ?? ""}
                onChange={(e) => setFieldValue("senderAdditionalNote", e.target.value)}
              />
            </Stack>
          {/* Supplier-only inputs (shipping + order-level discount) stay with the other
              inputs on the left so the ledger stays a pure display of computed values */}
          {!isBuyer && (
            <Box mt="md">
              <Grid gap="sm">
                <Grid.Col span={12}>
                  <NumberCurrencyField
                    max={1000000000}
                    value={values.shippingAmount}
                    onChange={(v) => setFieldValue("shippingAmount", v)}
                    error={errors?.shippingAmount}
                    label={t("supplierSales:sales.shippingFee")}
                    placeholder="0.00"
                    required
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Text fz={14} fw={600} c="gray.8" mb={6}>
                    {t("supplierSales:sales.priceDiscount")}
                  </Text>
                  <Flex gap={8}>
                    {values.discountType === "PERCENTAGE" ? (
                      <NumberInput
                        w={"60%"}
                        max={100}
                        disabled={!values.discountType}
                        value={values?.discountValue ?? ""}
                        onChange={(v) => setFieldValue("discountValue", v)}
                        label=""
                        placeholder="0.00"
                        hideControls
                        leftSection={<PercentIcon size={18} stroke="1.5" color="gray" opacity={0.8} />}
                      />
                    ) : (
                      <NumberCurrencyField
                        w={"60%"}
                        max={1000000000}
                        disabled={!values.discountType}
                        value={values.discountValue}
                        onChange={(v) => setFieldValue("discountValue", v)}
                        error={errors?.discountValue}
                        label=""
                        placeholder="0.00"
                        required
                      />
                    )}
                    <Select
                      label=""
                      value={values?.discountType ?? null}
                      onChange={(v) => {
                        setFieldValue("discountType", v)
                        setFieldValue("discountValue", undefined)
                      }}
                      data={[
                        { label: t("supplierSales:sales.discountPercent"), value: "PERCENTAGE" },
                        { label: t("supplierSales:sales.discountAmount"), value: "AMOUNT" },
                      ]}
                      placeholder={t("common:actions.select")}
                      clearable
                    />
                  </Flex>
                  {/* B.8 — the value came from the payment-term rule, and can be dropped */}
                  {/* Truthiness, not `!= null`: an unnamed term must not render
                      the note with an empty quoted name. */}
                  {autoFilledTermName ? (
                    <Flex align="center" gap="xs" mt="xs" wrap="wrap">
                      <Text fz={14} c="teal.8">
                        {t("supplierSales:sales.discountAutoFilled", { term: autoFilledTermName })}
                      </Text>
                      <Anchor
                        component="button"
                        type="button"
                        size="sm"
                        onClick={() => {
                          setFieldValue("discountType", undefined)
                          setFieldValue("discountValue", undefined)
                        }}
                      >
                        {t("supplierSales:sales.discountAutoFillClear")}
                      </Anchor>
                    </Flex>
                  ) : null}
                </Grid.Col>
              </Grid>
            </Box>
          )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
          <OrderLedger
            stats={[
              { value: len, label: t("supplierSales:sales.orderCount") },
              { value: totalQty, label: t("supplierSales:sales.totalQuantity") },
            ]}
            headLabel={t("supplierSales:sales.calculation")}
            rows={[
              { label: t("supplierSales:sales.orderTotalAmount"), value: currencyFormat(orderTotalAmount) },
              { label: t("supplierSales:sales.calculatedVat"), value: currencyFormat(calculatedVat), muted: true },
              { label: t("supplierSales:sales.calculatedTax"), value: currencyFormat(calculatedTax), muted: true },
              { label: t("supplierSales:sales.calculatedDiscount"), value: `− ${currencyFormat(ledgerDiscount)}`, negative: true },
              ...(isBuyer && orderLevelDiscount > 0
                ? [{
                  label   : `${t("order:buyerSales.termDiscountApplied")}${termDiscountSuffix(termDiscount)}`,
                  value   : `− ${currencyFormat(orderLevelDiscount)}`,
                  negative: true,
                }]
                : []),
              ...(isBuyer
                ? []
                : [{ label: t("supplierSales:sales.additionalTotalAmount"), value: currencyFormat(additionalTotal) }]),
            ]}
            grand={{
              label: t("supplierSales:sales.grandTotal"),
              value: currencyFormat(grandTotal),
            }}
            payRows={[
              {
                label: t("supplierSales:sales.paymentConfirmation"),
                value: codAdvance !== null ? currencyFormat(codAdvance) : "-",
              },
              {
                label: t("supplierSales:sales.paymentConfirmationDate"),
                value: paymentDateStr,
              },
            ]}
          />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* 4. Хавсралт нэмэх — slim card; the table appears once a row exists */}
      <Paper id="line-section-attachments" className={classes.sectionCard} data-flush data-active={(!isBuyer && activeSection === attachmentsIdx) || undefined} mb="md">
        <Group justify="space-between" wrap="nowrap" gap="sm" className={classes.cardHead}>
          <Box>
            <Text className={classes.cardTitle}>{t("supplierSales:sales.attachments")}</Text>
            {(values?.attachments || []).length === 0 && (
              <Text fz={14} c="gray.8">{t("common:table.noData")}</Text>
            )}
          </Box>
          <Button variant="default" leftSection={<PlusIcon size={16} />}
            onClick={() => {
              setFieldValue("attachments", [
                ...(values?.attachments || []),
                {
                  name: "",
                  unit: "",
                  price: 0,
                  quantity: 0,
                  discountType: "",
                  discountValue: 0,
                  isEdit: true,
                  isNew: true,
                },
              ])
            }}
          >
            {t("supplierSales:sales.attachments")}
          </Button>
        </Group>
        {(values?.attachments || []).length > 0 && (
          <Box className={classes.linesTable}>
            <Table columns={columnsAttachments} name="values.attachments" dataSource={values.attachments} pagination={false} />
          </Box>
        )}
      </Paper>
      <VariantSelectorDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        businessId={values?.supplierId}
        sellerBusinessId={sellerBizId!}
        buyerBusinessId={buyerBizId}
        orderType={isBuyer ? "PURCHASE" : "SALES"}
        selectedKeys={(values?.lines || []).map((l: any) => `${l.variantId}-${l.warehouseId}`)}
        onAdd={(line) => setFieldValue("lines", [...(values?.lines || []), line])}
        onAddAll={(lines) => setFieldValue("lines", [...(values?.lines || []), ...lines])}
      />
      <Modal
        opened={action[0] === "add" && values?.supplierId}
        onClose={() => {
          setAction([])
        }}
        withCloseButton={false}
        size="900px"
        padding={0}
        centered>
        <SupplierSalesAddDynamicAdditionalForm values={values} action={action} setFieldValue={setFieldValue} setAction={setAction} />
      </Modal>
    </Box>
  )
}

const useHeader = ({
  t,
  edit,
  imageColumn,
  values,
  setFieldValue,
  setEdit,
  onClick,
}: {
  t: any
  edit: any
  /** Picture in its own «Зураг» column (OrdB_SupplierOrder) instead of beside the name. */
  imageColumn: boolean
  values: any
  setFieldValue: any
  setEdit: any
  onClick: (e: any, e1: any) => void
}): ColumnType<ILinePlus & { isEdit: boolean }>[] => [
    {
      title: t("common:table.action"),
      render: (record, index) => {
        return (
          <ActionIcon onClick={() => onClick("remove", { ...record, index })} color="gray" variant="subtle" aria-label={t("common:actions.delete")}>
            <TrashIcon />
          </ActionIcon>
        )
      },
    },
    {
      title: "#",
      width: 30,
      render: (_r, index) => <RenderText text={index + 1} />,
    },
    ...(imageColumn
      ? [
          {
            title: t("supplierSales:sales.image"),
            render: (record: ILinePlus & { isEdit: boolean }) => (
              <Avatar size={44} src={record?.image || null} radius="sm">
                <ImageIcon />
              </Avatar>
            ),
          },
        ]
      : []),
    {
      title: t("supplierSales:sales.productService"),
      dataIndex: "name",
      render: (record, index) => (
        <Group gap="sm" wrap="nowrap">
          {!imageColumn && (
            <Avatar size={44} src={record?.image || null} radius="sm">
              <ImageIcon />
            </Avatar>
          )}
          <Box>
            {record.isEdit ? (
              <TextInput
                w={280}
                value={values?.lines?.[index]?.nameApp ?? ""}
                onChange={(e) => setFieldValue(`lines.${index}.nameApp`, e.target.value)}
                placeholder={t("supplierSales:sales.productService")}
              />
            ) : (
              <Text fz={14} fw={600}>{(record as any)?.nameMon || record?.name || "-"}</Text>
            )}
            <Text fz={14} c="gray.8" ff="monospace">{record?.skuCode || "-"}</Text>
          </Box>
        </Group>
      ),
    },
    {
      title: t("supplierSales:sales.warehouse"),
      dataIndex: "warehouseId",
      render: (record, index) => {
        const options =
          record.inventories?.map((inv: any) => ({
            value: inv.warehouseId,
            label: inv.warehouseName,
          })) ?? []

        const selectedWarehouse = values.lines[index]?.warehouseId
        if (!selectedWarehouse && options.length === 1) {
          setFieldValue(`lines.${index}.warehouseId`, options[0].value)
        }

        return (
          <Box style={{ width: 200 }}>
            <Select
              value={values?.lines?.[index]?.warehouseId ?? null}
              onChange={(v) => setFieldValue(`lines.${index}.warehouseId`, v)}
              placeholder={t("supplierSales:sales.selectWarehouse")}
              data={options}
              clearable
            />
          </Box>
        )
      },
    },
    {
      title: t("supplierSales:sales.orderQuantity"),
      dataIndex: "quantity",
      render: (record, index) => {
        return (
          <Flex align="center" gap="xs">
            {edit === index ? (
              <NumberInput
                max={9000000}
                size="sm"
                hideControls
                value={values?.lines?.[index]?.quantity ?? ""}
                onChange={(val: number | string) => {
                  const numVal = typeof val === "number" ? val : Number(val)
                  if (!numVal || numVal < 0) return
                  setFieldValue(`lines.${index}.quantity`, numVal)
                }}
                placeholder={t("supplierSales:sales.quantity")}
              />
            ) : (
              <Badge color="brand" size="md" radius="sm" variant="outline">
                {record?.quantity || 0}
              </Badge>
            )}
            <ActionIcon onClick={() => setEdit(edit !== index ? index : null)}>{edit === index ? <FloppyDiskIcon /> : <PencilSimpleIcon />}</ActionIcon>
          </Flex>
        )
      },
    },
    {
      title: t("supplierSales:sales.unitPrice"),
      dataIndex: "price",
      render: (record) => {
        return (
          <Group align="right">
            <Text size="sm" fw={500} c="var(--mantine-color-dark-5)" w="max-content">
              {currencyFormat(record?.price || 0)}
            </Text>
          </Group>
        )
      },
    },
    // DSC-001, per-line half. These four columns and the "Нийт дүн" below them
    // used `price / (vatPercent + 1)` — which equals the correct
    // `price * p / (100 + p)` only at p === 10 — and then added the extracted VAT
    // and city tax back on top of a line total that already contained them. The
    // ledger directly beneath this table is on the server's convention, so the
    // table had to move onto it too. See src/utils/order-amounts.ts.
    //
    // The figures here are per LINE: net of the line's own discount, before the
    // order-level discount, which the ledger shows as its own row.
    {
      title: t("supplierSales:sales.vatUnit"),
      render: (record) => <RenderText text={currencyFormat(unitVatAmount(record))} isAmount />,
    },
    {
      title: t("supplierSales:sales.totalVat"),
      dataIndex: "vatPercent",
      titleAlign: "right",
      render: (record) => <RenderText text={currencyFormat(lineVatAmount(record))} isAmount />,
    },
    {
      title: t("supplierSales:sales.taxUnit"),
      dataIndex: "",
      render: (record) => <RenderText text={currencyFormat(unitTaxAmount(record))} isAmount />,
    },
    {
      title: t("supplierSales:sales.totalTax"),
      dataIndex: "",
      render: (record) => <RenderText text={currencyFormat(lineTaxAmount(record))} isAmount />,
    },
    {
      title: t("supplierSales:sales.totalAmount"),
      dataIndex: "name",
      titleAlign: "right",
      render: (record: any) => <RenderText text={currencyFormat(lineNetAmount(record))} isAmount />,
    },
  ]

const useHeaderAdditional = ({
  t,
  edit,
  setEdit,
  onClick,
  setFieldValue,
  values,
  errors,
}: {
  t: any
  edit: any
  setEdit: any
  setFieldValue: any
  onClick: (e: any, e1: any) => void
  values: any
  errors: any
}): ColumnType<IAdditionalLine & { isEdit: boolean }>[] => [
    {
      title: t("common:table.action"),
      render: (record, index) => {
        return (
          <ActionIcon onClick={() => onClick("remove", { ...record, index })} color="gray" variant="subtle" aria-label={t("common:actions.delete")}>
            <TrashIcon />
          </ActionIcon>
        )
      },
    },
    {
      title: "#",
      width: 30,
      render: (_r, index) => <RenderText text={index + 1} />,
    },
    {
      title: t("supplierSales:sales.itemName"),
      dataIndex: "name",
      render: (record, index) => {
        if (record.isEdit) {
          return (
            <TextInput
              w={320}
              size="sm"
              value={values?.additionalLines?.[index]?.name ?? ""}
              onChange={(e) => setFieldValue(`additionalLines.${index}.name`, e.target.value)}
              placeholder={t("supplierSales:sales.enterName")}
            />
          )
        } else return <RenderText text={record?.name || "-"} />
      },
    },
    {
      title: t("supplierSales:sales.unit"),
      dataIndex: "unit",
      render: (record, index) => {
        if (record.isEdit) {
          return (
            <TextInput
              w={320}
              size="sm"
              value={values?.additionalLines?.[index]?.unit ?? ""}
              onChange={(e) => setFieldValue(`additionalLines.${index}.unit`, e.target.value)}
              placeholder={t("supplierSales:sales.unitPlaceholder")}
            />
          )
        } else return <RenderText text={record?.unit || "-"} />
      },
    },
    {
      title: t("supplierSales:sales.unitPrice"),
      dataIndex: "price",
      render: (record, index) => {
        if (record.isEdit) return (
          <NumberCurrencyField
            max={9999999999}
            size="sm"
            value={values?.additionalLines?.[index]?.price}
            onChange={(v) => setFieldValue(`additionalLines.${index}.price`, v)}
            error={errors?.[`additionalLines.${index}.price`]}
            placeholder="0.00"
          />
        )
        else return <RenderText text={currencyFormat(record?.price || 0)} isAmount />
      },
    },
    {
      title: t("supplierSales:sales.quantity"),
      dataIndex: "quantity",
      render: (record, index) => {
        if (record.isEdit) return (
          <NumberInput
            max={9000000}
            size="sm"
            min={0}
            hideControls
            value={values?.additionalLines?.[index]?.quantity ?? ""}
            onChange={(v) => setFieldValue(`additionalLines.${index}.quantity`, v)}
            placeholder="0"
          />
        )
        else
          return (
            <Flex align="center" gap="xs">
              {edit === index ? (
                <NumberInput
                  max={9000000}
                  min={0}
                  size="sm"
                  hideControls
                  value={values?.additionalLines?.[index]?.quantity ?? ""}
                  onChange={(v) => setFieldValue(`additionalLines.${index}.quantity`, v)}
                  placeholder={t("supplierSales:sales.quantity")}
                />
              ) : (
                <Badge color="brand" size="md" radius="sm" variant="outline">
                  {record.quantity || "0"}
                </Badge>
              )}
              <ActionIcon onClick={() => setEdit(edit !== index ? index : null)}>{edit === index ? <FloppyDiskIcon /> : <PencilSimpleIcon />}</ActionIcon>
            </Flex>
          )
      },
    },
    {
      title: t("supplierSales:sales.discountType"),
      dataIndex: "discountType",
      render: (record, index) => {
        if (record.isEdit)
          return (
            <Select
              label=""
              size="sm"
              value={values?.additionalLines?.[index]?.discountType ?? null}
              onChange={(v) => {
                setFieldValue(`additionalLines.${index}.discountType`, v)
                setFieldValue(`additionalLines.${index}.discountValue`, undefined)
              }}
              data={[
                { label: t("supplierSales:sales.discountPercent"), value: "PERCENTAGE" },
                { label: t("supplierSales:sales.discountAmount"), value: "AMOUNT" },
              ]}
              placeholder={t("common:actions.select")}
              clearable
            />
          )
        else
          return (
            <Group align="center">
              {edit === index ? (
                <Select
                  label=""
                  size="sm"
                  value={values?.additionalLines?.[index]?.discountType ?? null}
                  onChange={(v) => {
                    setFieldValue(`additionalLines.${index}.discountType`, v)
                    setFieldValue(`additionalLines.${index}.discountValue`, undefined)
                  }}
                  data={[
                    { label: t("supplierSales:customer.discountPercent"), value: "PERCENTAGE" },
                    { label: t("supplierSales:customer.discountAmount"), value: "AMOUNT" },
                  ]}
                  placeholder={t("common:actions.select")}
                  clearable
                />
              ) : (
                <Text w={150}>
                  <Badge color="brand" size="md" radius="sm" variant="outline">
                    {record.discountType === "PERCENTAGE" && t("supplierSales:sales.discountPercent")}
                    {record.discountType === "AMOUNT" && t("supplierSales:sales.discountAmount")}
                    {!record?.discountType && "-"}
                  </Badge>
                </Text>
              )}
            </Group>
          )
      },
    },
    {
      title: t("supplierSales:sales.discount"),
      dataIndex: "discountValue",
      render: (record, index) => {
        return (
          <>
            {record?.discountType === "AMOUNT" && (
              <NumberCurrencyField
                max={1000000000}
                label=""
                placeholder="0.00"
                value={values?.additionalLines?.[index]?.discountValue}
                onChange={(v) => setFieldValue(`additionalLines.${index}.discountValue`, v)}
                error={errors?.[`additionalLines.${index}.discountValue`]}
              />
            )}
            {record?.discountType === "PERCENTAGE" && (
              <NumberInput
                max={100}
                value={values?.additionalLines?.[index]?.discountValue ?? ""}
                onChange={(v) => setFieldValue(`additionalLines.${index}.discountValue`, v)}
                label=""
                placeholder="0.00"
                hideControls
                leftSection={<PercentIcon size={18} stroke="1.5" color="gray" opacity={0.8} />}
              />
            )}
            {!record?.discountType && (
              <NumberCurrencyField
                max={1000000000}
                disabled
                label=""
                placeholder="0.00"
                value={values?.additionalLines?.[index]?.discountValue}
                onChange={(v) => setFieldValue(`additionalLines.${index}.discountValue`, v)}
                error={errors?.[`additionalLines.${index}.discountValue`]}
              />
            )}
          </>
        )
      },
    },
    {
      title: t("supplierSales:sales.amount"),
      dataIndex: "name",
      titleAlign: "right",
      render: (record: any) => {
        let count
        count =
          (record?.price || 0) * (record?.quantity || 0) -
          (record?.discountType === "PERCENTAGE" ? ((record?.price || 0) * (record?.quantity || 0) * record.discountValue) / 100 : record?.discountValue)

        return <RenderText text={currencyFormat(count || 0)} isAmount />
      },
    },
  ]

const useHeaderAttachments = ({
  t,
  edit,
  setEdit,
  values,
  setFieldValue,
  errors,
  onClick,
}: {
  t: any
  edit: any
  setEdit: any
  values: any
  setFieldValue: any
  errors: any
  onClick: (e: any, e1: any) => void
}): ColumnType<IAttachment & { isEdit: boolean }>[] => [
  {
    title: t("common:actions.action"),
    width: 50,
    render: (record, index) => {
      return (
        <Tooltip label={t("common:actions.delete")}>
          <ActionIcon onClick={() => onClick("remove", { ...record, index })} color="gray" variant="subtle" aria-label={t("common:actions.delete")}>
            <TrashIcon />
          </ActionIcon>
        </Tooltip>
      )
    },
  },
  {
    title: "#",
    width: 30,
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: t("supplierSales:sales.fileUrl"),
    dataIndex: "url",
    render: (record, index) => {
      if (record.isEdit) {
        return (
          <FileField
            value={values.attachments?.[index]?.url}
            onChange={(v) => setFieldValue(`attachments.${index}.url`, v)}
            error={errors[`attachments.${index}.url`]}
            placeholder={t("supplierSales:sales.fileUrl")}
            label={""}
          />
        )
      } else return <RenderText text={record?.url || "-"} />
    },
  },
  {
    title: t("supplierSales:sales.name"),
    dataIndex: "name",
    render: (record, index) => {
      if (record.isEdit) {
        return (
          <TextInput
            value={values?.attachments?.[index]?.name ?? ""}
            onChange={(e) => setFieldValue(`attachments.${index}.name`, e.target.value)}
            placeholder={t("supplierSales:sales.namePlaceholder")}
          />
        )
      } else return <RenderText text={record?.name || "-"} />
    },
  },
  {
    title: t("supplierSales:sales.description"),
    dataIndex: "description",
    render: (record, index) => {
      if (record.isEdit) {
        return (
          <TextInput
            value={values?.attachments?.[index]?.description ?? ""}
            onChange={(e) => setFieldValue(`attachments.${index}.description`, e.target.value)}
            placeholder={t("supplierSales:sales.descriptionPlaceholder")}
          />
        )
      } else return <RenderText text={record?.description || "-"} />
    },
  },
]

